import { useState, useRef, useCallback, useMemo, useEffect, SyntheticEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tabs,
  Tab,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SaveIcon from '@mui/icons-material/Save';
import { useLanguage } from '../../../../context/LanguageContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import CommunicationDefinitionStep, { ChannelType, DefinitionFormData } from './CommunicationDefinitionStep';
import CommunicationTargetingStep, { TargetingFormData } from './CommunicationTargetingStep';
import CommunicationCreationStep, { CreationStepHandle } from './CommunicationCreationStep';
import CommunicationOffersStep, { OffersStepHandle, OfferFormData, emptyOffer, offerInputToForm } from './CommunicationOffersStep';
import CommunicationValidationStep from './CommunicationValidationStep';
import DiscardChangesDialog from './DiscardChangesDialog';
import ChangeChannelDialog from './ChangeChannelDialog';
import BatPreviewDialog from './BatPreviewDialog';
import { useCreateCommunicationInput, useCreateOfferInput, useUpdateCommunicationInput, useDownloadDevis, useDownloadCgv, useValidateCommunication } from '../../../../hooks/Marketing';
import { CommunicationInputData } from '../../../../hooks/Marketing/useGetLatestAnimations';
import { toast } from 'react-toastify';

const TRIGGER_ID_MAP: Record<string, number> = {
  montant: 1, article: 2, points: 3, passage: 4, action: 5, inscription: 6,
};

const PREREQ_ID_MAP: Record<string, number> = {
  montant: 1, article: 2, points: 3, immediat: 4,
};

const NO_DISCOUNT_ID = -1;

const CHANNEL_ID: Record<ChannelType, number> = {
  courrier: 1, email: 2, sms: 4, cashdesk: 8, wallet: 9, '': 0,
};

const CHANNEL_BY_ID: Record<number, ChannelType> = {
  1: 'courrier', 2: 'email', 4: 'sms', 8: 'cashdesk', 9: 'wallet'
};

interface CommunicationDialogProps {
  open: boolean;
  onClose: () => void;
  animationInputId: number | null;
  onEnsureAnimationId?: () => Promise<number | null>;
  onSaved?: () => void;
  editingComm?: CommunicationInputData | null;
}

const ALL_STEPS = [
  { key: 'definition', label: 'Définition', Icon: HomeIcon },
  { key: 'creation', label: 'Création', Icon: BorderColorIcon },
  { key: 'ciblage', label: 'Ciblage', Icon: TrackChangesIcon },
  { key: 'offers', label: 'Offre(s)', Icon: CardGiftcardIcon },
  { key: 'validation', label: 'Validation', Icon: VerifiedIcon },
] as const;

type StepKey = (typeof ALL_STEPS)[number]['key'];

const INITIAL_DEFINITION: DefinitionFormData = {
  channel: '', title: '', description: '', sendDate: '', sendByCustomer: false,
  hasOffer: true, followUpDateStart: '', followUpDateEnd: '', isTestOperation: false,
  isRecurring: false, editionDateStart: '', editionDateEnd: '',
};

const toDatetimeLocal = (s: string | null | undefined): string => {
  if (!s) return '';
  if (s.includes('T')) return s.slice(0, 16);
  if (s.length >= 10) return s.slice(0, 10) + 'T00:00';
  return '';
};

const INITIAL_TARGETING: TargetingFormData = {
  title: '', description: '', queryId: null, queryDescription: '', nbRecipients: null, listId: null,
};

function CommunicationDialog({
  open, onClose, animationInputId, onEnsureAnimationId, onSaved, editingComm,
}: CommunicationDialogProps) {
  const { t } = useLanguage();
  const { sidebarColor } = useLayoutPreferences();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [definition, setDefinition] = useState<DefinitionFormData>(() =>
    editingComm
      ? {
          channel: CHANNEL_BY_ID[editingComm.defChannelId] ?? '',
          title: editingComm.defTitle,
          description: editingComm.defDescription ?? '',
          sendDate: toDatetimeLocal(editingComm.defSendStart),
          sendByCustomer: editingComm.defChannelId === CHANNEL_ID['cashdesk'],
          hasOffer: (editingComm.creationPayload?._hasOffer as boolean | undefined) ?? true,
          followUpDateStart: (editingComm.creationPayload?._followUpDateStart as string | undefined) ?? '',
          followUpDateEnd: (editingComm.creationPayload?._followUpDateEnd as string | undefined) ?? '',
          isTestOperation: (editingComm.creationPayload?._isTestOperation as boolean | undefined) ?? false,
          isRecurring: (editingComm.creationPayload?._isRecurring as boolean | undefined) ?? false,
          editionDateStart: (editingComm.creationPayload?._editionDateStart as string | undefined) ?? '',
          editionDateEnd: (editingComm.creationPayload?._editionDateEnd as string | undefined) ?? '',
        }
      : INITIAL_DEFINITION
  );
  const [targeting, setTargeting] = useState<TargetingFormData>(() =>
    editingComm
      ? {
          title: editingComm.targetTitle ?? '',
          description: editingComm.targetDescription ?? '',
          queryId: editingComm.targetQueryId ?? null,
          queryDescription: (editingComm.creationPayload?._queryDesc as string) ?? '',
          nbRecipients: null,
          listId: (editingComm.creationPayload?._listId as string | number | undefined) ?? null,
        }
      : INITIAL_TARGETING
  );
  const [communicationInputId, setCommunicationInputId] = useState<number | null>(
    () => editingComm?.id ?? null
  );
  const [isDirty, setIsDirty] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [pendingChannel, setPendingChannel] = useState<ChannelType | null>(null);
  const [hasCreation, setHasCreation] = useState(false);
  const [batAcknowledged, setBatAcknowledged] = useState(false);
  const [cgvAcknowledged, setCgvAcknowledged] = useState(false);
  const [batPreviewOpen, setBatPreviewOpen] = useState(false);
  const [previewingBat, setPreviewingBat] = useState(false);
  const [batContent, setBatContent] = useState<{ mode: 'unlayer' | 'html'; html: string; subject: string } | null>(null);
  const [validationOffers, setValidationOffers] = useState<OfferFormData[]>([]);
  const [mountedSteps, setMountedSteps] = useState<Set<StepKey>>(() => new Set(['definition']));
  const [localOffers, setLocalOffers] = useState<OfferFormData[]>(() =>
    editingComm?.offerInputs && editingComm.offerInputs.length > 0
      ? editingComm.offerInputs.map(offerInputToForm)
      : [emptyOffer('1')]
  );
  const [usedOfferIndices, setUsedOfferIndices] = useState<Set<number>>(new Set());

  const visibleSteps = useMemo(
    () => ALL_STEPS.filter(s => {
      // Wallet communications never carry an offer (Tikeo notifications aren't coupon-based), so the
      // tab is dropped entirely for that channel rather than just left empty.
      if (s.key === 'offers') return definition.hasOffer && definition.channel !== 'wallet';
      // Courrier communications are produced externally, so there is no template to create here.
      if (s.key === 'creation') return definition.channel !== 'courrier';
      return true;
    }),
    [definition.hasOffer, definition.channel]
  );

  // If the "Offre(s)" tab disappears (communication marked as having no offer) while it was
  // selected or past it, keep activeStep pointing at a step that still exists.
  useEffect(() => {
    setActiveStep(step => Math.min(step, visibleSteps.length - 1));
  }, [visibleSteps.length]);

  const creationPayloadRef = useRef<Record<string, unknown> | null>(
    editingComm?.creationPayload ?? null
  );

  const creatingRef = useRef(false);
  const offersRef = useRef<OffersStepHandle>(null);
  const creationStepRef = useRef<CreationStepHandle>(null);

  const { createCommunicationInput, isLoading: saving, error: saveError } = useCreateCommunicationInput();
  const { updateCommunicationInput } = useUpdateCommunicationInput();
  const { createOfferInput } = useCreateOfferInput();
  const { downloadDevis, loading: downloadingDevis } = useDownloadDevis();
  const { downloadCgv, loading: downloadingCgv } = useDownloadCgv();
  const { validateCommunication, error: validateCommunicationError } = useValidateCommunication();

  const handlePreviewBat = useCallback(async () => {
    setPreviewingBat(true);
    try {
      const content = await creationStepRef.current?.getEmailContent();
      if (!content) {
        toast.info('Aperçu du BAT non disponible pour ce canal.');
        return;
      }
      setBatContent(content);
      setBatPreviewOpen(true);
    } finally {
      setPreviewingBat(false);
    }
  }, []);

  const handleDownloadDevis = useCallback(async () => {
    const ok = await downloadDevis({
      title: definition.title,
      channel: definition.channel,
      sendDate: definition.sendDate,
      targetingSummary: targeting.queryDescription
        ? `Requête de ciblage : ${targeting.queryDescription}`
        : 'Aucun ciblage défini.',
      nbRecipients: targeting.nbRecipients,
    });
    if (!ok) toast.error('Erreur lors du téléchargement du devis.');
  }, [downloadDevis, definition, targeting]);

  const handleDownloadCgv = useCallback(async () => {
    const ok = await downloadCgv({ channel: definition.channel });
    if (!ok) toast.error('Erreur lors du téléchargement des CGV.');
  }, [downloadCgv, definition.channel]);

  const applyChannelChange = useCallback((channel: ChannelType) => {
    setDefinition(prev => ({ ...prev, channel }));
    setLocalOffers([emptyOffer('1')]);
    setUsedOfferIndices(new Set());
    setMountedSteps(new Set(['definition']));
    setIsDirty(true);
  }, []);

  const handleDefinitionChange = useCallback((patch: Partial<DefinitionFormData>) => {
    if (patch.channel !== undefined && patch.channel !== definition.channel) {
      if (definition.channel !== '') {
        setPendingChannel(patch.channel);
        return;
      }
      applyChannelChange(patch.channel);
      return;
    }
    setDefinition(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  }, [definition.channel, applyChannelChange]);

  const handleConfirmChannelChange = useCallback(() => {
    if (pendingChannel !== null) applyChannelChange(pendingChannel);
    setPendingChannel(null);
  }, [pendingChannel, applyChannelChange]);

  const handleCancelChannelChange = useCallback(() => {
    setPendingChannel(null);
  }, []);

  const handleTargetingChange = useCallback((patch: Partial<TargetingFormData>) => {
    setTargeting(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  }, []);

  const canSaveDefinition =
    definition.channel !== '' && definition.title.trim() !== '';

  // Les communications Caisse n'ont pas d'envoi à proprement parler : la date de début
  // d'édition du coupon en tient lieu pour defSendStart.
  const resolvedSendStart = definition.channel === 'cashdesk'
    ? (definition.editionDateStart || undefined)
    : (definition.sendDate || undefined);

  const ensureCommunicationSaved = async (): Promise<number | null> => {
    if (communicationInputId !== null) return communicationInputId;
    if (creatingRef.current) return null;
    if (!canSaveDefinition) return null;

    const animId = animationInputId ?? await onEnsureAnimationId?.();
    if (animId === null || animId === undefined) return null;

    creatingRef.current = true;
    try {
      const result = await createCommunicationInput({
        animationInput: `/api/animation_inputs/${animId}`,
        defTitle: definition.title.trim(),
        defDescription: definition.description.trim() || undefined,
        defChannelId: CHANNEL_ID[definition.channel],
        defSendStart: resolvedSendStart,
        defNotSendable: definition.sendByCustomer ? 1 : 0,
        targetTitle: targeting.title.trim() || undefined,
        targetDescription: targeting.description.trim() || undefined,
        targetQueryId: targeting.queryId ?? undefined,
        creationPayload: {
          ...(creationPayloadRef.current ?? {}),
          _queryDesc: targeting.queryDescription.trim() || undefined,
          _listId: targeting.listId ?? undefined,
          _hasOffer: definition.hasOffer,
          _followUpDateStart: definition.hasOffer ? undefined : (definition.followUpDateStart || undefined),
          _followUpDateEnd: definition.hasOffer ? undefined : (definition.followUpDateEnd || undefined),
          _isTestOperation: definition.isTestOperation,
          _isRecurring: definition.isRecurring,
          _editionDateStart: definition.editionDateStart || undefined,
          _editionDateEnd: definition.editionDateEnd || undefined,
        },
      });
      if (result) {
        setCommunicationInputId(result.id);
        return result.id;
      }
      return null;
    } finally {
      creatingRef.current = false;
    }
  };

  const patchAllData = async (commId: number): Promise<void> => {
    await updateCommunicationInput(commId, {
      defTitle: definition.title.trim(),
      defDescription: definition.description.trim() || undefined,
      defSendStart: resolvedSendStart,
      defNotSendable: definition.sendByCustomer ? 1 : 0,
      targetTitle: targeting.title.trim() || undefined,
      targetDescription: targeting.description.trim() || undefined,
      targetQueryId: targeting.queryId ?? undefined,
      creationPayload: {
        ...(creationPayloadRef.current ?? {}),
        _queryDesc: targeting.queryDescription.trim() || undefined,
        _listId: targeting.listId ?? undefined,
        _hasOffer: definition.hasOffer,
        _followUpDateStart: definition.hasOffer ? undefined : (definition.followUpDateStart || undefined),
        _followUpDateEnd: definition.hasOffer ? undefined : (definition.followUpDateEnd || undefined),
        _isTestOperation: definition.isTestOperation,
        _isRecurring: definition.isRecurring,
        _editionDateStart: definition.editionDateStart || undefined,
        _editionDateEnd: definition.editionDateEnd || undefined,
      },
    });
  };

  const goToStep = (step: number) => {
    const key = visibleSteps[step]?.key;
    setMountedSteps(prev => (key ? new Set(prev).add(key) : prev));
    if (key === 'validation') setValidationOffers(localOffers);
    setActiveStep(step);
  };

  const handleTabChange = (_: SyntheticEvent, newStep: number) => {
    goToStep(newStep);
    setDraftError('');
  };

  const handleNext = () => {
    if (activeStep < visibleSteps.length - 1) {
      goToStep(activeStep + 1);
      setDraftError('');
    }
  };

  const hasUnsavedData =
    (communicationInputId === null && (definition.title.trim() !== '' || definition.channel !== '')) ||
    (communicationInputId !== null && isDirty);

  const resetDialog = () => {
    creatingRef.current = false;
    creationPayloadRef.current = null;
    setActiveStep(0);
    setDefinition(INITIAL_DEFINITION);
    setTargeting(INITIAL_TARGETING);
    setCommunicationInputId(null);
    setIsDirty(false);
    setDraftError('');
    setHasCreation(false);
    setBatAcknowledged(false);
    setCgvAcknowledged(false);
    setValidationOffers([]);
    setLocalOffers([emptyOffer('1')]);
    setUsedOfferIndices(new Set());
    setMountedSteps(new Set(['definition']));
  };

  const handleClose = () => {
    if (hasUnsavedData) {
      setConfirmCloseOpen(true);
      return;
    }
    resetDialog();
    onClose();
  };

  const handleConfirmDiscard = () => {
    setConfirmCloseOpen(false);
    resetDialog();
    onClose();
  };

  const handleConfirmSaveDraft = async () => {
    setConfirmCloseOpen(false);
    await handleSaveDraft();
  };

  const saveOffersForComm = async (commId: number) => {
    const offers: OfferFormData[] = offersRef.current?.getOffers() ?? localOffers;
    const hasContent = (o: OfferFormData) =>
      o.title.trim() !== '' || o.description.trim() !== '' || o.offerNumber.trim() !== '';
    const toSave = offers.filter(hasContent);
    await Promise.all(
      toSave.map(o =>
        createOfferInput({
          communicationInput: `/api/communication_inputs/${commId}`,
          title: o.title.trim() || undefined,
          description: o.description.trim() || undefined,
          offerNumber: o.offerNumber.trim() || undefined,
          startValidityDate: o.startValidityDate || undefined,
          endValidityDate: o.endValidityDate || undefined,
          nbMaxEdit: o.nbMaxEdit ? Number(o.nbMaxEdit) : undefined,
          nbMaxUse: o.nbMaxUse ? Number(o.nbMaxUse) : undefined,
          refDiscountTypeId: o.discountTypeIds[0] === NO_DISCOUNT_ID ? undefined : o.discountTypeIds[0],
          payloadRefDiscountType: o.discountTypeIds.length > 0 && o.discountTypeIds[0] !== NO_DISCOUNT_ID ? {
            remiseMode: o.remiseMode,
            remiseValue: o.remiseValue,
            pointsMode: o.pointsMode,
            pointsValue: o.pointsValue,
          } : undefined,
          refEditConditionId: o.triggerId ? TRIGGER_ID_MAP[o.triggerId] : undefined,
          payloadRefEditCondition: (o.triggerId || definition.editionDateStart || definition.editionDateEnd) ? {
            triggerMontantMin: o.triggerMontantMin,
            triggerMontantMax: o.triggerMontantMax,
            triggerArticleMin: o.triggerArticleMin,
            triggerArticleMax: o.triggerArticleMax,
            triggerPointsMin: o.triggerPointsMin,
            triggerPointsMax: o.triggerPointsMax,
            editStartDate: definition.editionDateStart,
            editEndDate: definition.editionDateEnd,
          } : undefined,
          refUseConditionId: o.prereqId ? PREREQ_ID_MAP[o.prereqId] : undefined,
          payloadRefUseCondition: o.prereqId ? {
            prereqMontantMin: o.prereqMontantMin,
            prereqMontantMax: o.prereqMontantMax,
            prereqArticleMin: o.prereqArticleMin,
            prereqArticleMax: o.prereqArticleMax,
            prereqPointsMin: o.prereqPointsMin,
            prereqPointsMax: o.prereqPointsMax,
          } : undefined,
        }),
      ),
    );
  };

  const persistCommunication = async (errorMessage: string): Promise<number | null> => {
    if (!canSaveDefinition) {
      setDraftError('Veuillez saisir un titre et sélectionner un canal avant d\'enregistrer.');
      return null;
    }
    setSavingDraft(true);
    setDraftError('');
    try {
      if (definition.channel === 'email') {
        const emailContent = await creationStepRef.current?.getEmailContent();
        if (emailContent) {
          creationPayloadRef.current = {
            ...(creationPayloadRef.current ?? {}),
            emailEditorMode: emailContent.mode,
            emailHtml: emailContent.html,
            ...(emailContent.design ? { emailDesign: emailContent.design } : {}),
          };
        }
      }
      if (definition.channel === 'sms' && creationPayloadRef.current?.enrichi) {
        const landingPageContent = await creationStepRef.current?.getSmsLandingPageContent();
        if (landingPageContent) {
          creationPayloadRef.current = {
            ...(creationPayloadRef.current ?? {}),
            landingPageEditorMode: landingPageContent.mode,
            landingPageHtml: landingPageContent.html,
            ...(landingPageContent.design ? { landingPageDesign: landingPageContent.design } : {}),
          };
        }
      }
      const commId = await ensureCommunicationSaved();
      if (commId === null) {
        setDraftError(errorMessage);
        return null;
      }
      if (communicationInputId !== null) await patchAllData(commId);
      if (definition.hasOffer) await saveOffersForComm(commId);
      return commId;
    } finally {
      setSavingDraft(false);
    }
  };

  const handleTerminate = async () => {
    const commId = await persistCommunication('Erreur lors de l\'enregistrement de la communication.');
    if (commId === null) return;

    // Vérifie auprès du service ApiCheckOperation que l'opération est correctement configurée —
    // le backend bloque la validation (422) si le check échoue.
    const result = await validateCommunication({
      communicationInputId: commId,
      acceptBATVisualization: batAcknowledged ? 1 : 0,
      acceptCGV: cgvAcknowledged ? 1 : 0,
      needImmediateExtract: 0,
    });
    if (!result) {
      setDraftError(validateCommunicationError || "Erreur lors de la vérification de l'opération.");
      return;
    }

    resetDialog();
    onSaved?.();
    onClose();
  };

  const handleSaveDraft = async () => {
    const ok = await persistCommunication('Erreur lors de l\'enregistrement du brouillon.');
    if (!ok) return;
    resetDialog();
    onSaved?.();
    onClose();
  };

  return (
  <>
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          width: fullScreen ? '100%' : '97vw',
          maxWidth: fullScreen ? '100%' : '97vw',
          height: fullScreen ? '100%' : '96vh',
          maxHeight: fullScreen ? '100%' : '96vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #e9ecef', flexShrink: 0 }}>
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {t('pages.marketing.communication.editionTitle')}
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', flex: '1 1 auto', minHeight: 0 }}>
        <Tabs
          orientation="vertical"
          value={activeStep}
          onChange={handleTabChange}
          sx={{
            width: fullScreen ? 72 : 200,
            minWidth: fullScreen ? 72 : 200,
            borderRight: '1px solid #dee2e6',
            backgroundColor: '#f8f9fa',
            '& .MuiTabs-indicator': { backgroundColor: sidebarColor, left: 0, right: 'auto', width: 3 },
            '& .MuiTab-root': {
              minHeight: 72,
              textTransform: 'none',
              alignItems: 'center',
              justifyContent: fullScreen ? 'center' : 'flex-start',
              flexDirection: 'row',
              gap: 1.5,
              px: fullScreen ? 1 : 2.5,
              minWidth: fullScreen ? 72 : undefined,
              color: '#6c757d',
              fontWeight: 500,
              '&.Mui-selected': { color: sidebarColor, fontWeight: 700 },
              '&.Mui-disabled': { opacity: 0.4 },
            },
          }}
        >
          {visibleSteps.map(({ key, label, Icon }, idx) => (
            <Tab
              key={key}
              label={fullScreen ? undefined : label}
              icon={
                <Icon
                  fontSize="small"
                  sx={{ color: activeStep === idx ? sidebarColor : '#adb5bd', mr: 0 }}
                />
              }
              iconPosition={fullScreen ? undefined : 'start'}
              aria-label={fullScreen ? label : undefined}
            />
          ))}
        </Tabs>

        <Box sx={{ flexGrow: 1, overflow: 'auto', height: '100%', scrollbarGutter: 'stable' }}>
          {saveError && <Alert severity="error" sx={{ m: 2 }}>{saveError}</Alert>}

          <Box sx={{ display: visibleSteps[activeStep]?.key === 'definition' ? 'block' : 'none' }}>
            <CommunicationDefinitionStep data={definition} onChange={handleDefinitionChange} />
          </Box>
          <Box sx={{ display: visibleSteps[activeStep]?.key === 'creation' ? 'block' : 'none' }}>
            {mountedSteps.has('creation') && (
              <CommunicationCreationStep
                ref={creationStepRef}
                channel={definition.channel}
                onHasContent={setHasCreation}
                offerCount={localOffers.length}
                offerTitles={localOffers.map(o => o.title.trim())}
                onAddOffer={() => setLocalOffers(prev => [...prev, emptyOffer(String(Date.now()))])}
                onOfferUsed={idx => setUsedOfferIndices(prev => new Set([...prev, idx]))}
                onOfferIndicesChange={setUsedOfferIndices}
                onOfferRemoved={idx => setLocalOffers(prev => prev.filter((_, i) => i !== idx))}
                initialPayload={editingComm?.creationPayload ?? null}
                onPayloadChange={payload => {
                  creationPayloadRef.current = payload;
                  if (payload.type === 'sms') {
                    const msg = (payload.message as string) || '';
                    const indices = new Set<number>();
                    let m: RegExpExecArray | null;
                    const re = /#NUM_COUPON_(\d+)#/g;
                    while ((m = re.exec(msg)) !== null) indices.add(parseInt(m[1]));
                    setUsedOfferIndices(indices);
                  }
                }}
              />
            )}
          </Box>
          <Box sx={{ display: visibleSteps[activeStep]?.key === 'ciblage' ? 'block' : 'none' }}>
            {mountedSteps.has('ciblage') && <CommunicationTargetingStep data={targeting} onChange={handleTargetingChange} />}
          </Box>
          {definition.hasOffer && (
            <Box sx={{ display: visibleSteps[activeStep]?.key === 'offers' ? 'block' : 'none' }}>
              {mountedSteps.has('offers') && (
                <CommunicationOffersStep
                  ref={offersRef}
                  offers={localOffers}
                  onOffersChange={offers => { setLocalOffers(offers); setIsDirty(true); }}
                  usedOfferIndices={usedOfferIndices}
                  channel={definition.channel}
                  isRecurring={definition.isRecurring}
                />
              )}
            </Box>
          )}
          <Box sx={{ display: visibleSteps[activeStep]?.key === 'validation' ? 'block' : 'none' }}>
            {mountedSteps.has('validation') && (
              <CommunicationValidationStep
                definition={definition}
                targeting={targeting}
                offers={validationOffers}
                hasCreation={hasCreation}
                onBatAcknowledged={setBatAcknowledged}
                onPreviewBat={handlePreviewBat}
                previewingBat={previewingBat}
                onDownloadDevis={handleDownloadDevis}
                downloadingDevis={downloadingDevis}
                onCgvAcknowledged={setCgvAcknowledged}
                onDownloadCgv={handleDownloadCgv}
                downloadingCgv={downloadingCgv}
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e9ecef', px: 3, py: 1.5, flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
        {draftError && (
          <Alert severity="error" onClose={() => setDraftError('')} sx={{ mb: 1, py: 0.25 }}>
            {draftError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
          <Box>
            {communicationInputId !== null && (
              <Typography variant="caption" color="text.secondary">
                Communication #{communicationInputId} {editingComm ? '(édition)' : 'créée'}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
            {activeStep > 0 && (
              <Button
                variant="text"
                size="small"
                onClick={() => setActiveStep(s => s - 1)}
                sx={{ color: '#6c757d', textTransform: 'none' }}
              >
                Précédent
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={savingDraft ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveDraft}
              disabled={savingDraft || saving}
              sx={{ color: '#6c757d', borderColor: '#dee2e6', textTransform: 'none', fontWeight: 500 }}
            >
              Enregistrer le brouillon
            </Button>
            {activeStep === visibleSteps.length - 1 ? (
              (() => {
                const needsAck = definition.channel !== 'courrier';
                const blocked = needsAck && (!batAcknowledged || !cgvAcknowledged);
                return (
                  <Tooltip
                    title={blocked ? 'Veuillez cocher "J\'ai pris connaissance du BAT" et des CGV avant de terminer' : ''}
                    disableHoverListener={!blocked}
                  >
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={savingDraft ? <CircularProgress size={14} color="inherit" /> : undefined}
                        onClick={handleTerminate}
                        disabled={saving || savingDraft || blocked}
                        sx={{ color: sidebarColor, borderColor: sidebarColor, textTransform: 'none', fontWeight: 600 }}
                      >
                        Terminer
                      </Button>
                    </span>
                  </Tooltip>
                );
              })()
            ) : (
              <Button
                variant="contained"
                endIcon={saving ? <CircularProgress size={14} color="inherit" /> : <ArrowForwardIcon />}
                onClick={handleNext}
                disabled={saving || savingDraft}
                sx={{
                  backgroundColor: sidebarColor,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: sidebarColor, opacity: 0.9 },
                }}
              >
                {t('pages.marketing.communication.nextStep')}
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>

    <DiscardChangesDialog
      open={confirmCloseOpen}
      onClose={() => setConfirmCloseOpen(false)}
      onSaveDraft={handleConfirmSaveDraft}
      onDiscard={handleConfirmDiscard}
      savingDraft={savingDraft}
    />

    <BatPreviewDialog
      open={batPreviewOpen}
      onClose={() => setBatPreviewOpen(false)}
      content={batContent}
    />

    <ChangeChannelDialog
      open={pendingChannel !== null}
      onCancel={handleCancelChannelChange}
      onConfirm={handleConfirmChannelChange}
    />
  </>
  );
}

export default CommunicationDialog;
